import TemplateProcessor from 'stated-js';
import { Emitter, EventType } from 'mitt';
import { useSyncExternalStore } from 'react';
import { logger } from '../../../utils';

export let emitter: Emitter<Record<EventType, unknown>> | null = null;

// Manage TemplateProcessor list, key is id.
export const templateProcessorList: Record<string, TemplateProcessor> = {};

export const addTemplateProcessor = (id: string, processor: TemplateProcessor) => {
  templateProcessorList[id] = processor;
  emitter?.emit('templateProcessorListChanged');
};

export const removeTemplateProcessor = (id: string) => {
  if (templateProcessorList[id]) {
    delete templateProcessorList[id];
    emitter?.emit('templateProcessorListChanged');
  }
};

const getSnapshot = () => templateProcessorList;

const subscribe = (handleStoreChange: () => void) => {
  const eventHandler = () => handleStoreChange();
  emitter?.on('templateProcessorListChanged', eventHandler);
  return () => emitter?.off('templateProcessorListChanged', eventHandler);
};

export const useTemplateProcessorList = () => {
  const processorsList = useSyncExternalStore(subscribe, getSnapshot);
  return processorsList;
};

// Globals

type GlobalsRecord = Record<string, unknown>;

let globals: Readonly<GlobalsRecord> = {};

export const getGlobals = (): Readonly<GlobalsRecord> => {
  return globals;
};

export const initGlobals = (initialGlobals: GlobalsRecord, callback?: (isInitialized: boolean) => void): void => {
  globals = { ...initialGlobals };
  if (callback) {
    callback(true);
  }
  logger.log('initialized globals', globals);
};

export const setGlobals = (path: string, data: unknown) => {
  _updateGlobals(path, data);
  Object.values(templateProcessorList).forEach(templateProcessor => {
    templateProcessor?.setData(path, data);
  });

  emits('globalsChanged', { path, data, globals });
};

// WARNING! For internal use only (do not export) - update the globals variable

const _updateGlobals = (path: string, data: unknown) => {
  const segments = path.split('/').filter(Boolean);
  if (segments[0] === 'globals') {
    segments.shift();
  }
  let current: GlobalsRecord = globals;
  segments.slice(0, -1).forEach((segment, index) => {
    if (!(segment in current)) {
      current[segment] = {};
    } else if (typeof current[segment] !== 'object' || current[segment] === null) {
      throw new Error(
        `Attempted to set nested global at ${segments.slice(0, index + 1).join('/')} but found non-object.`,
      );
    }
    current = current[segment] as GlobalsRecord;
  });

  const finalSegment = segments[segments.length - 1];
  if (finalSegment) {
    current[finalSegment] = data;
  }
};

// Global Emitter

export const emits = async (event: string, data: any): Promise<void> => {
  emitter && (emitter as Emitter<Record<EventType, unknown>>).emit(event, data);
};

export const initEmitter = (_emitter: Emitter<Record<EventType, unknown>> | null) => {
  emitter = _emitter;
};

export const mergeTemplateGlobals = (templateGlobals: GlobalsRecord | undefined): void => {
  const flatGlobals: GlobalsRecord = flattenGlobals(templateGlobals, '/globals');
  Object.entries(flatGlobals).forEach(([path, value]) => {
    setGlobals(path, value);
  });
};

const flattenGlobals = (globalsObj: GlobalsRecord | undefined, prefix: string = ''): GlobalsRecord => {
  if (!globalsObj) return {};
  return Object.keys(globalsObj).reduce<GlobalsRecord>((acc, key) => {
    const fullPath: string = `${prefix}/${key}`;
    const value = globalsObj[key];
    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(acc, flattenGlobals(value as GlobalsRecord | undefined, fullPath));
    } else {
      acc[fullPath] = value;
    }
    return acc;
  }, {});
};
